import moment from 'moment';
import { UtilSelect } from './UtilSelect';
/**
 * SightingPopup
 *
 * Pure (DOM-free) derivation of the sighting map-popover field rows. Extracted
 * from Sighting._buildSightingPopup so the many conditional fields and the two
 * JSON id-map lookups are unit-testable and the render method stays small. Row
 * labels are English keys (translated by the renderer); empty values are kept
 * here and filtered out at render time.
 */
export class SightingPopup {
    /**
     * rows — the [labelKey, value] popover rows for a sighting, in display order.
     * @param {SightingsEntry} entry
     * @param {SightingPopupLookups} lookups
     * @returns {[string, string][]}
     */
    static rows(entry, lookups) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const date = entry.date ? moment(entry.date.split(' ')[0]).format('YYYY.MM.DD') : '';
        const vehicleName = SightingPopup._lookupName(entry.vehicle_id, lookups.vehicles, (v) => v.name);
        const driverName = SightingPopup._lookupName(entry.vehicle_driver_id, lookups.drivers, (v) => v.user.name);
        const orgStr = SightingPopup._lookupName(entry.organization_id, lookups.organizations, (v) => v.description);
        const reactionStr = SightingPopup._lookupName(entry.reaction_id, lookups.encounterCategories, (v) => v.name);
        const timeStr = ((_a = entry.tour_start) !== null && _a !== void 0 ? _a : entry.tour_end)
            ? `${(_b = entry.tour_start) !== null && _b !== void 0 ? _b : ''} – ${(_c = entry.tour_end) !== null && _c !== void 0 ? _c : ''}`
            : '';
        const distanceNum = Number(entry.distance_coast);
        const distanceStr = entry.distance_coast && Number.isFinite(distanceNum)
            ? `${distanceNum.toFixed(2)} mi`
            : (_d = entry.distance_coast) !== null && _d !== void 0 ? _d : '';
        const otherSpeciesNames = SightingPopup.parseIdMapNames((_e = entry.other_species) !== null && _e !== void 0 ? _e : '', (id) => { var _a; return (_a = lookups.species.get(id)) === null || _a === void 0 ? void 0 : _a.name.split(',')[0]; });
        const behaviourNames = SightingPopup.parseIdMapNames((_f = entry.behaviours) !== null && _f !== void 0 ? _f : '', (id) => { var _a; return (_a = lookups.behaviours.get(id)) === null || _a === void 0 ? void 0 : _a.name; });
        return [
            ['Date', date],
            ['Sighting', `#${entry.id} · Tour #${entry.tour_id}`],
            ['Time', timeStr],
            ['Other species', otherSpeciesNames.join(', ')],
            ['Group-Size', SightingPopup._groupSizeStr(entry)],
            ['Subgroups', entry.subgroups ? UtilSelect.getSelectStr(entry.subgroups) : ''],
            ['Behaviour', behaviourNames.join(', ')],
            ['Reaction', reactionStr],
            ['Vehicle', vehicleName],
            ['Driver', driverName],
            ['Organization', orgStr],
            ['Beaufort', (_g = entry.beaufort_wind) !== null && _g !== void 0 ? _g : ''],
            ['Distance', distanceStr],
            ['Photos', entry.photo_taken ? UtilSelect.getSelectStr(entry.photo_taken) : ''],
            ['Note', (_h = entry.note) !== null && _h !== void 0 ? _h : '']
        ];
    }
    /**
     * _lookupName — resolve an optional foreign key to a display name via its
     * lookup map, or '' when the id is absent/unknown.
     * @param {number | undefined} id
     * @param {Map<number, T>} map
     * @param {(value: T) => string} field
     * @returns {string}
     * @private
     */
    static _lookupName(id, map, field) {
        if (!id) {
            return '';
        }
        const value = map.get(id);
        return value ? field(value) : '';
    }
    /**
     * _groupSizeStr — "count (n juv, n calves, n newborn)" or '' when the group
     * size is unknown.
     * @param {SightingsEntry} entry
     * @returns {string}
     * @private
     */
    static _groupSizeStr(entry) {
        if (entry.species_count === undefined) {
            return '';
        }
        const extra = [];
        if (entry.juveniles !== undefined && entry.juveniles > 0) {
            extra.push(`${entry.juveniles} juv`);
        }
        if (entry.calves !== undefined && entry.calves > 0) {
            extra.push(`${entry.calves} calves`);
        }
        if (entry.newborns !== undefined && entry.newborns > 0) {
            extra.push(`${entry.newborns} newborn`);
        }
        return `${entry.species_count}${extra.length > 0 ? ` (${extra.join(', ')})` : ''}`;
    }
    /**
     * parseIdMapNames — resolve a JSON id-map column (`{"0":"5",...}`) to the
     * list of names the ids map to, skipping unknown ids. Malformed JSON yields
     * an empty list.
     * @param {string} json
     * @param {(id: number) => string | undefined} resolve
     * @returns {string[]}
     */
    static parseIdMapNames(json, resolve) {
        const names = [];
        let data;
        try {
            data = JSON.parse(json);
        }
        catch (_a) {
            return names;
        }
        if (!data || typeof data !== 'object') {
            return names;
        }
        for (const value of Object.values(data)) {
            if (typeof value === 'string') {
                const name = resolve(parseInt(value, 10));
                if (name !== undefined) {
                    names.push(name);
                }
            }
        }
        return names;
    }
}
